/**
 * État vide. Dit toujours **pourquoi** c’est vide et **quoi faire ensuite** —
 * une liste vide sans explication laisse croire à une panne.
 */

import type { ReactNode } from 'react'

import { cn } from '../lib/cn'

export interface EmptyStateProps {
  readonly titre: ReactNode
  readonly description?: ReactNode | undefined
  /** Bouton d’action principale (« Créer une saison »). */
  readonly action?: ReactNode | undefined
  /** Icône décorative ; une par défaut si rien n’est fourni. */
  readonly icone?: ReactNode | undefined
  readonly className?: string | undefined
}

export function EmptyState({ titre, description, action, icone, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}
    >
      <span className="text-slate-300" aria-hidden="true">
        {icone ?? <IconeVide />}
      </span>
      <p className="text-sm font-semibold text-slate-900">{titre}</p>
      {description !== undefined && (
        <p className="max-w-prose text-sm text-slate-500">{description}</p>
      )}
      {action !== undefined && <div className="mt-1">{action}</div>}
    </div>
  )
}

function IconeVide() {
  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="size-10"
    >
      <rect x="7" y="12" width="34" height="26" rx="3" />
      <path d="M7 20h34M16 12v-3M32 12v-3" strokeLinecap="round" />
    </svg>
  )
}

/**
 * Bandeau d’erreur d’un écran : affiche le **message métier** du backend,
 * jamais un texte générique (ARCHITECTURE §9.4).
 */
export interface BandeauErreurProps {
  readonly titre?: ReactNode | undefined
  readonly message: ReactNode
  readonly action?: ReactNode | undefined
  readonly className?: string | undefined
}

export function BandeauErreur({ titre, message, action, className }: BandeauErreurProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-wrap items-start gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900',
        className,
      )}
    >
      <svg
        viewBox="0 0 20 20"
        fill="currentColor"
        className="mt-0.5 size-5 shrink-0 text-red-600"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M10 1.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17ZM9 6a1 1 0 1 1 2 0v5a1 1 0 1 1-2 0V6Zm1 9.5a1.1 1.1 0 1 1 0-2.2 1.1 1.1 0 0 1 0 2.2Z"
          clipRule="evenodd"
        />
      </svg>
      <div className="min-w-0 flex-1">
        {titre !== undefined && <p className="font-semibold">{titre}</p>}
        <div className={cn(titre !== undefined && 'mt-0.5')}>{message}</div>
      </div>
      {action !== undefined && <div className="shrink-0">{action}</div>}
    </div>
  )
}
